import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BasicButton from '@components/BasicButton';
import Tag from './Tag';
import ObjectItem from './ObjectItem';
import SearchField from '@core/dashboard/components/SearchField';

import { useSearch } from './hooks';
import { useTrainerToolsContent } from '@features/TrainerTools/provider';
import { useTrainerTools } from '@features/TrainerTools';
import { Screens } from '@features/TrainerTools/interfaces';
import { IEventAsset } from '@providers/EventProvider/interfaces';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { useMytaverse } from '@providers/MytaverseProvider';
import { DEFAULT_HEIGHT, DEFAULT_SIZE } from '@features/TrainerTools/constants';
import { useStyles } from './styles';

const Collection = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [presentNewObject, setPresentNewObject] = useState(false);

  const { setIsObjectShareActive } = useTrainerTools();

  const {
    clearFilters,
    activeTags,
    objects,
    setActiveScreen,
    activeObject,
    activeObjectReady,
    temporaryObject,
    setActiveObject,
    setActiveObjectReady,
    tags,
    isExploration,
    setIsExploration,
    setHeight,
    setSize,
  } = useTrainerToolsContent();

  const { sendMessageToUnreal } = useMytaverse();

  const {
    inputValue,
    filteredData: filteredFiles,
    handleInputChange,
    handleInputClear,
  } = useSearch<IEventAsset>(objects, 'name', activeTags);

  const handleNext = useCallback(() => {
    if (temporaryObject && activeObject?.assetId !== temporaryObject?.assetId) {
      setPresentNewObject(true);
      setActiveObjectReady(false);

      setActiveObject((prevState) => {
        if (prevState && isExploration) {
          setIsExploration((prevState) => !prevState);
        }

        sendMessageToUnreal({
          action: WebsocketAction.Asset3DLoad,
          payload: {
            assetId: temporaryObject.assetId,
            url: temporaryObject.data?.url,
          },
        });
        return temporaryObject;
      });

      setHeight(DEFAULT_HEIGHT);
      setSize(DEFAULT_SIZE);
      setIsObjectShareActive(true);
    } else {
      setActiveScreen(Screens.SETTINGS);
    }
  }, [activeObject, temporaryObject, isExploration]);

  useEffect(() => {
    if (activeObject && activeObjectReady && presentNewObject) {
      setActiveScreen(Screens.SETTINGS);
    }
  }, [activeObject, activeObjectReady, presentNewObject]);

  return (
    <Box sx={classes.root}>
      <Box sx={classes.header}>
        <Typography sx={classes.title}>
          {translate('trainerToolModal.title_second')}
        </Typography>

        <SearchField
          value={inputValue}
          onChange={handleInputChange}
          onClear={handleInputClear}
          placeholder={'Search entry'}
          sx={classes.search}
        />
      </Box>

      <Box>
        <Box sx={classes.filter}>
          {tags.map((tag) => (
            <Tag tag={tag} key={tag} />
          ))}
        </Box>
      </Box>

      <Box sx={classes.group}>
        <Typography sx={classes.groupName}>
          {translate('trainerToolModal.collection.title')}:
        </Typography>
      </Box>

      <Box sx={classes.list}>
        {filteredFiles.map((item) => (
          <ObjectItem key={item.assetId} item={item} />
        ))}
        {filteredFiles.length === 0 ? (
          <Typography sx={classes.text}>
            {translate('trainerToolModal.empty')}
          </Typography>
        ) : null}
      </Box>

      <Box sx={classes.actions}>
        <BasicButton
          onClick={() => {
            clearFilters();
          }}
          white
          disabled={tags.length === 0}
        >
          {translate('trainerToolModal.collection.clear')}
        </BasicButton>
        <BasicButton
          onClick={handleNext}
          primary
          disabled={!temporaryObject}
          loading={!!activeObject && !activeObjectReady && presentNewObject}
        >
          {translate('trainerToolModal.collection.present')}
        </BasicButton>
      </Box>
    </Box>
  );
};

export default Collection;
